/**
 * Timezone-select helpers for the scheduling calendars.
 *
 * The option LIST is not built here — it comes from the shared
 * `useTzOptions` hook (BE `time_zones` via `GET /me/edit`, with
 * `TIME_ZONE_CONSTANTS` as fallback), the same source Profile settings and
 * every other picker in the app uses. This module only resolves which of
 * those options the calendar toolbar should show as selected.
 */
import { normalizeTimeZoneForAtsCalendarApi } from '@/api/calendarEvents';

export interface TimezoneOption {
  value: string;
  label: string;
}

/**
 * Resolves the value the timezone `Select` should render as selected.
 *
 * `normalizeTimeZoneForAtsCalendarApi` exists to map legacy aliases the
 * browser reports (`Asia/Calcutta`) onto the canonical key our option
 * list carries (`Asia/Kolkata`). But `timezone-support`'s tzdata also
 * collapses zones we list *separately*: `Asia/Singapore` canonicalises
 * to `Asia/Kuala_Lumpur`, so normalising unconditionally made picking
 * Singapore snap the dropdown straight back to Kuala Lumpur.
 *
 * So: if the zone is already one of our options, keep it verbatim, and
 * only fall back to normalisation for values the list doesn't carry.
 */
export function resolveTimezoneOptionValue(
  timezone: string,
  options: TimezoneOption[]
): string {
  const trimmed = timezone?.trim();
  if (trimmed && options.some((o) => o.value === trimmed)) return trimmed;
  return normalizeTimeZoneForAtsCalendarApi(trimmed ?? '');
}
